// src/pages/LeadDetailPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Divider,
  Chip,
  Grid,
  CircularProgress,
  IconButton,
  useMediaQuery,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Breadcrumbs,
  Link,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  LEAD_STATUS_DEFINITIONS,
  getLeadStatusColor,
} from "../constants/leadStatuses";
import LeadStatusIcon from "../components/LeadStatusIcon";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import MapIcon from "@mui/icons-material/Map";
import DeleteIcon from "@mui/icons-material/Delete";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import HomeIcon from "@mui/icons-material/Home";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import RestoreIcon from "@mui/icons-material/Restore";
import ArchiveIcon from "@mui/icons-material/Archive";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import API from "../utils/api";
import { emitLeadUpdate } from "../utils/leadSync";
import { toast } from "react-toastify";
import StatusUpdateDialog from "../components/StatusUpdateDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import ActivityTimeline from "../components/ActivityTimeline";
import useAuth from "../hooks/useAuth";
import { useGlobalCounts } from "../context/GlobalCountContext";
import { displayLeadCategoryLabel } from "../utils/leadCategoryLabel";
import {
  getLeadCategoryOutlineColor,
  leadCategoryChipSx,
} from "../utils/leadCategoryChip";
import { useLeadDetailReturn } from "../utils/leadDetailNavigation";
import {
  isAssignableRepRole,
  buildAssignedRepOptions,
  formatAssignedRepOptionLabel,
} from "../utils/assignedRepOptions";

export default function LeadDetailPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { fromLabel, goBack } = useLeadDetailReturn();
  const { id } = useParams();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, role, can, isReadOnly, canViewAdditionalNotes } = useAuth();
  const { refreshCounts } = useGlobalCounts();
  
  // Admin users should bypass read-only restrictions
  const isEditDisabled = isReadOnly && role !== "Admin";
  
  // Admin/Sales Director can edit any lead; Managers can edit leads in their territories;
  // Sales Reps can edit their own assigned leads.
  const canEditLead =
    can.editAnyLead || can.editLeadsInOwnTerritory || can.editOwnLeads;
  const canManageLeadCategory = role === "Admin" || role === "Sales Director" || role === "Manager";
  const canDeleteLead =
    (role === "Admin" || role === "Sales Director") && !isReadOnly;

  // ── Header button styles ──────────────────────────────────────────────────
  const headerGhostButtonSx = {
    minHeight: 42,
    borderRadius: "14px",
    px: 1.6,
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.875rem",
    letterSpacing: "-0.01em",
    border: (theme) =>
      theme.palette.mode === "dark"
        ? "1px solid rgba(255,255,255,0.10)"
        : "1px solid rgba(15,23,42,0.08)",
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.05)"
        : "rgba(255,255,255,0.72)",
    color: "text.primary",
    backdropFilter: "blur(10px)",
    boxShadow: (theme) =>
      theme.palette.mode === "dark"
        ? "0 8px 20px rgba(0,0,0,0.24)"
        : "0 8px 20px rgba(15,23,42,0.06)",
    "&:hover": {
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.08)"
          : "rgba(255,255,255,0.92)",
      transform: "translateY(-1px)",
    },
  };

  const headerOutlineButtonSx = {
    minHeight: 42,
    borderRadius: "14px",
    px: 1.8,
    textTransform: "none",
    fontWeight: 800,
    fontSize: "0.875rem",
    letterSpacing: "-0.01em",
    borderColor: "rgba(37,99,235,0.28)",
    color: "primary.main",
    background: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(37,99,235,0.08)"
        : "rgba(37,99,235,0.04)",
    "&:hover": {
      borderColor: "primary.main",
      background: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(37,99,235,0.14)"
          : "rgba(37,99,235,0.08)",
      transform: "translateY(-1px)",
    },
  };

  const headerPrimaryButtonSx = {
    minHeight: 42,
    borderRadius: "14px",
    px: 1.9,
    textTransform: "none",
    fontWeight: 800,
    fontSize: "0.875rem",
    letterSpacing: "-0.01em",
    background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
    color: "#fff",
    boxShadow: "0 10px 22px rgba(37,99,235,0.24)",
    "&:hover": {
      background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
      boxShadow: "0 14px 28px rgba(37,99,235,0.34)",
      transform: "translateY(-1px)",
    },
  };
  // ─────────────────────────────────────────────────────────────────────────

  const proposalControlSx = {
    borderRadius: "12px",
    minHeight: 46,
    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(15,23,42,0.82)"
        : "rgba(255,255,255,0.96)",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(255,255,255,0.2)"
          : "rgba(15,23,42,0.16)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(96,165,250,0.7)"
          : "rgba(37,99,235,0.5)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "primary.main",
      borderWidth: 1,
    },
    "&.Mui-focused": {
      boxShadow: (theme) =>
        `0 0 0 4px ${alpha(theme.palette.primary.main, 0.16)}`,
    },
  };

  const proposalSelectSx = {
    ...proposalControlSx,
    "& .MuiSelect-select": {
      fontSize: "0.88rem",
      fontWeight: 500,
      py: 1.25,
    },
  };

  const proposalTextFieldSx = {
    "& .MuiOutlinedInput-root": proposalControlSx,
    "& .MuiInputBase-input": {
      fontSize: "0.88rem",
      fontWeight: 500,
    },
  };

  const proposalMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.75,
        borderRadius: 2,
        p: 0.5,
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.12)"
            : "rgba(15,23,42,0.08)",
        boxShadow: "0 18px 40px rgba(15,23,42,0.18)",
        "& .MuiMenuItem-root": {
          minHeight: 36,
          borderRadius: 1.25,
          my: 0.25,
          fontSize: "0.84rem",
          fontWeight: 500,
          px: 1.25,
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          },
        },
      },
    },
  };

  const STATUS_OPTIONS = LEAD_STATUS_DEFINITIONS.map((d) => ({
    value: d.value,
    icon: d.Icon,
    color: d.color,
    label: d.value,
  }));

  const [lead, setLead] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingContact, setEditingContact] = useState(false);
  const [contactData, setContactData] = useState({ first_name: "", last_name: "", email: "", phone: "", assigned_rep: "", status: "", category: "", followUpDate: "", sms_email_opt_in: "" });
  const [leadCategories, setLeadCategories] = useState([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState("");
  const [markFollowUpComplete, setMarkFollowUpComplete] = useState(false);
  const [editingAdditionalNotes, setEditingAdditionalNotes] = useState(false);
  const [additionalNotesText, setAdditionalNotesText] = useState("");
  const [users, setUsers] = useState([]);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [restoringLead, setRestoringLead] = useState(false);
  
  // Proposal state
  const [showProposal, setShowProposal] = useState(false);
  const [proposalItems, setProposalItems] = useState([]);
  const [activeProposalId, setActiveProposalId] = useState(null);
  const [activeProposalStatus, setActiveProposalStatus] = useState("draft");
  const [productsCatalog, setProductsCatalog] = useState([]);
  const [packagesCatalog, setPackagesCatalog] = useState([]);
  const [addingProductId, setAddingProductId] = useState("");
  const [addingPackageId, setAddingPackageId] = useState("");
  const [proposalCategoryFilter, setProposalCategoryFilter] = useState("");
const [monthlyServiceCharge, setMonthlyServiceCharge] = useState(null);
const [selectedMonthlyCharge, setSelectedMonthlyCharge] = useState(null);
const [monthlyServiceLabel, setMonthlyServiceLabel] = useState("");
const [showProposalSummary, setShowProposalSummary] = useState(false);
const [leadProposals, setLeadProposals] = useState([]);

// Quick View (Proposal Preview)
const [quickViewOpen, setQuickViewOpen] = useState(false);
const [quickViewHtml, setQuickViewHtml] = useState(null);
const [quickViewLoading, setQuickViewLoading] = useState(false);
const [quickViewError, setQuickViewError] = useState(null);
const [lastPreviewProposal, setLastPreviewProposal] = useState(null);

// Discount (one-time)
const [proposalDiscount, setProposalDiscount] = useState(0);
const [selectedDiscount, setSelectedDiscount] = useState(null);
const [proposalSpecialTerms, setProposalSpecialTerms] = useState("");
const [fillQuickConfigured, setFillQuickConfigured] = useState(false);
const [confirmWithdrawOpen, setConfirmWithdrawOpen] = useState(false);
const [withdrawProposalId, setWithdrawProposalId] = useState(null);

const getNormalizedProductType = (item) =>
  String(item?.product_type || "").trim().toLowerCase();

const normalizeCategoryWhitespace = (value) =>
  String(value || "").trim().replace(/\s+/g, " ");

const normalizeCategoryKey = (value) =>
  normalizeCategoryWhitespace(value).toLowerCase();

const getNormalizedCategory = (item) =>
  normalizeCategoryKey(item?.category);

const mapNewProposalItemsToRows = (items = [], status = "draft") =>
  items.map((item) => ({
    id: item.id,
    product: item.product
      ? {
          id: item.product,
          sku: item.sku_snapshot || item.product_sku || "",
        }
      : null,
    product_name: item.name_snapshot || item.product_name || "Unknown Product",
    quantity: item.quantity || 1,
    unit_price: item.unit_price_snapshot ?? 0,
    status,
  }));

const toProposalHistoryRow = (proposal) => {
  const formatDateTime = (iso) => {
    if (!iso) return "Not sent";
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? "Not sent" : d.toLocaleString();
  };

  const status = String(proposal?.status || "draft").toLowerCase();
  const statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
  const statusColor =
    status === "accepted"
      ? "success"
      : status === "sent"
      ? "info"
      : status === "withdrawn"
      ? "warning"
      : "default";

  return {
    id: proposal.id,
    proposalId: proposal.id,
    status,
    statusLabel,
    statusColor,
    sentAtLabel: formatDateTime(proposal.sent_at || proposal.created_at),
    totalLabel: `$${parseFloat(proposal.total || 0).toFixed(2)}`,
    toEmail: proposal.to_email || "—",
    createdBy: proposal.created_by_name || "—",
  };
};

const resetProposalBuilderState = () => {
  setProposalItems([]);
  setMonthlyServiceCharge(null);
  setSelectedMonthlyCharge(null);
  setMonthlyServiceLabel("");
  setProposalDiscount(0);
  setSelectedDiscount(null);
  setProposalSpecialTerms("");
};

const loadLeadProposalsFromNewApi = async (leadId) => {
  const res = await API.get(`/leads/${leadId}/proposals/`);
  const payload = res?.data;
  const proposals = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.results)
      ? payload.results
      : [];
  setLeadProposals(proposals);
  return proposals;
};





const openQuickView = async (proposal) => {
  const proposalId = proposal?.proposalId || proposal?.id;
  if (!proposalId) {
    console.warn("Quick View blocked: missing proposal.id", proposal);
    return;
  }

  setLastPreviewProposal(proposal);
  setQuickViewOpen(true);
  setQuickViewLoading(true);
  setQuickViewError(null);

  try {
    const res = await API.get(`/proposals/${proposalId}/preview/`);
    setQuickViewHtml(res.data);
  } catch {
    setQuickViewError("Failed to load proposal preview.");
  } finally {
    setQuickViewLoading(false);
  }
};



const closeQuickView = () => {
  setQuickViewOpen(false);
  setQuickViewHtml(null);
  setQuickViewError(null);
};

const handleWithdrawProposal = async () => {
  const pid = withdrawProposalId;
  if (!pid || !lead?.id) return;
  try {
    await API.post(`/proposals/${pid}/withdraw/`);
    toast.success("Proposal withdrawn.");
    const proposals = await loadLeadProposalsFromNewApi(lead.id);
    const updated = proposals.find((x) => x.id === pid);
    if (updated && activeProposalId === pid) {
      setActiveProposalStatus(updated.status);
    }
  } catch (err) {
    console.error(err);
    toast.error(
      err?.response?.data?.detail || "Failed to withdraw proposal."
    );
  } finally {
    setConfirmWithdrawOpen(false);
    setWithdrawProposalId(null);
  }
};

const proposalHistoryViewButtonSx = {
  textTransform: "none",
  fontSize: "0.75rem",
  fontWeight: 600,
  minHeight: 32,
  px: 1.25,
  borderColor: "primary.main",
  color: "primary.main",
  "&:hover": {
    bgcolor: "primary.main",
    color: "primary.contrastText",
    borderColor: "primary.main",
    "& .MuiButton-startIcon": { color: "primary.contrastText" },
  },
};

const proposalHistory = React.useMemo(() => {
  return leadProposals
    .filter((proposal) => proposal.status !== "draft")
    .map(toProposalHistoryRow);
}, [leadProposals]);


const renderDesktopRows = () => (
  <Box sx={{ display: "flex", flexDirection: "column" }}>
    {proposalHistory.map((item) => (
      <Box
        key={item.id}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={item.statusLabel} color={item.statusColor} />
            <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
              {item.totalLabel}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: "0.76rem", color: "text.secondary" }}>
            Sent: {item.sentAtLabel} · To: {item.toEmail}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
            sx={proposalHistoryViewButtonSx}
            onClick={(e) => {
              e.stopPropagation();
              openQuickView(item);
            }}
          >
            View
          </Button>
          {item.status === "sent" && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={isEditDisabled}
              startIcon={<CancelIcon fontSize="small" />}
              sx={{ textTransform: "none", fontSize: "0.75rem", minHeight: 32 }}
              onClick={(e) => {
                e.stopPropagation();
                setWithdrawProposalId(item.proposalId);
                setConfirmWithdrawOpen(true);
              }}
            >
              Withdraw
            </Button>
          )}
        </Stack>
      </Box>
    ))}
  </Box>
);


const renderMobileRows = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
    {proposalHistory.map((item) => (
      <Box
        key={item.id}
        sx={{
          py: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip size="small" label={item.statusLabel} color={item.statusColor} />
            <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
              {item.totalLabel}
            </Typography>
          </Stack>
          <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }}>
            {item.sentAtLabel}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<VisibilityOutlinedIcon fontSize="small" />}
              sx={proposalHistoryViewButtonSx}
              onClick={(e) => {
                e.stopPropagation();
                openQuickView(item);
              }}
            >
              View
            </Button>
            {item.status === "sent" && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={isEditDisabled}
                startIcon={<CancelIcon fontSize="small" />}
                sx={{ textTransform: "none", fontSize: "0.75rem", minHeight: 32 }}
                onClick={(e) => {
                  e.stopPropagation();
                  setWithdrawProposalId(item.proposalId);
                  setConfirmWithdrawOpen(true);
                }}
              >
                Withdraw
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    ))}
  </Box>
);

  
  // Compute proposal status from backend data
  const proposalStatus = React.useMemo(() => {
    return activeProposalStatus || "draft";
  }, [activeProposalStatus]);

// ---------------- Proposal totals (one-time)
const proposalSubtotal = React.useMemo(() => {
  return proposalItems.reduce(
    (sum, item) =>
      sum + parseFloat(item.unit_price || 0) * (item.quantity || 1),
    0
  );
}, [proposalItems]);

const proposalTotal = React.useMemo(() => {
  return Math.max(
    proposalSubtotal - (proposalDiscount || 0),
    0
  );
}, [proposalSubtotal, proposalDiscount]);

  const proposalMonthlyLineLabel = React.useMemo(() => {
    const fromCatalog = selectedMonthlyCharge?.name?.trim();
    const custom = monthlyServiceLabel?.trim();
    if (fromCatalog) return fromCatalog;
    if (custom) return custom;
    return "Monthly service";
  }, [selectedMonthlyCharge, monthlyServiceLabel]);

  const showProposalMonthlySummaryLine = React.useMemo(() => {
    if (selectedMonthlyCharge) return true;
    if (monthlyServiceLabel?.trim()) return true;
    if (
      monthlyServiceCharge != null &&
      monthlyServiceCharge !== "" &&
      !Number.isNaN(Number(monthlyServiceCharge))
    ) {
      return true;
    }
    return false;
  }, [monthlyServiceCharge, selectedMonthlyCharge, monthlyServiceLabel]);

  const proposalMonthlyLineAmountText = React.useMemo(() => {
    if (
      monthlyServiceCharge == null ||
      monthlyServiceCharge === "" ||
      Number.isNaN(Number(monthlyServiceCharge))
    ) {
      return "—";
    }
    return `$${Number(monthlyServiceCharge).toFixed(2)}/mo`;
  }, [monthlyServiceCharge]);


  const token = localStorage.getItem("access_token");

  // Status is now edited in Contact Information card

  useEffect(() => {
    if (id) {
      fetchLead();
    }
  }, [id]);

  useEffect(() => {
    const loadFillQuickConfig = async () => {
      try {
        const [companyRes, userRes] = await Promise.all([
          API.get("/company-profile/"),
          API.get("/users/me/"),
        ]);
        const hasCompanyConfig =
          Boolean((companyRes.data?.fillquick_user_id || "").trim()) &&
          Boolean((companyRes.data?.fillquick_api_key || "").trim());
        const hasUserConfig =
          Boolean((userRes.data?.fillquick_user_id || "").trim()) &&
          Boolean(userRes.data?.fillquick_configured || false);
        const hasConfig = hasCompanyConfig || hasUserConfig;
        setFillQuickConfigured(hasConfig);
      } catch {
        setFillQuickConfigured(false);
      }
    };
    loadFillQuickConfig();
  }, []);

  // Fetch users/reps for assignment dropdown
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await API.get("/users/");
        // Include assignable reps: Sales + Manager roles (active and inactive).
        // Inactive users stay in state so a current inactive assignee can still display.
        // Managers are intentionally assignable for escalation workflows (e.g. at-risk leads).
        const assignableUsers = (Array.isArray(res.data) ? res.data : []).filter(
          isAssignableRepRole
        );
        setUsers(assignableUsers);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchLeadCategories = async () => {
      if (!canManageLeadCategory) return;
      try {
        const res = await API.get("/lead-categories/");
        const rows = Array.isArray(res.data) ? res.data : (res.data?.results || []);
        setLeadCategories(rows);
      } catch (err) {
        console.error("Error fetching lead categories:", err);
      }
    };
    fetchLeadCategories();
  }, [canManageLeadCategory]);

  // Load product catalog (once)
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        let nextPath = "/settings/products-services/";
        const collected = [];

        while (nextPath) {
          const res = await API.get(nextPath);
          const payload = res?.data;

          if (Array.isArray(payload)) {
            collected.push(...payload);
            nextPath = null;
            continue;
          }

          const pageRows = Array.isArray(payload?.results) ? payload.results : [];
          collected.push(...pageRows);

          const nextUrl = payload?.next;
          if (!nextUrl) {
            nextPath = null;
          } else {
            // Axios baseURL already points to API root.
            // Keep only the relative path part so this works across environments.
            nextPath = nextUrl.includes("/api/")
              ? nextUrl.split("/api/")[1]
              : nextUrl;
          }
        }

        setProductsCatalog(collected);
      } catch (err) {
        console.error("Failed to load products", err);
      }
    };
    const fetchPackages = async () => {
      try {
        let nextPath = "/settings/product-packages/";
        const collected = [];
        while (nextPath) {
          const res = await API.get(nextPath);
          const payload = res?.data;
          if (Array.isArray(payload)) {
            collected.push(...payload);
            nextPath = null;
            continue;
          }
          const pageRows = Array.isArray(payload?.results) ? payload.results : [];
          collected.push(...pageRows);
          const nextUrl = payload?.next;
          if (!nextUrl) {
            nextPath = null;
          } else {
            nextPath = nextUrl.includes("/api/")
              ? nextUrl.split("/api/")[1]
              : nextUrl;
          }
        }
        setPackagesCatalog(collected);
      } catch (err) {
        console.error("Failed to load product packages", err);
      }
    };
    fetchProducts();
    fetchPackages();
  }, []);

  useEffect(() => {
    setAddingProductId("");
    setAddingPackageId("");
  }, [proposalCategoryFilter]);

  // Monthly charges and discounts: show full catalog for these types only.
  // Do not apply proposalCategoryFilter here — that filter narrows *line items*;
  // discount/monthly rows often use different categories than equipment SKUs, which
  // made these dropdowns look empty when a product category was selected.
  const monthlyChargeProducts = React.useMemo(() => {
    return productsCatalog.filter(
      (p) => getNormalizedProductType(p) === "monthly_charge"
    );
  }, [productsCatalog]);

  const discountProducts = React.useMemo(() => {
    return productsCatalog.filter(
      (p) => getNormalizedProductType(p) === "discount"
    );
  }, [productsCatalog]);

  const proposalPackagesCategoryKey = React.useMemo(
    () => normalizeCategoryKey("Packages / kits"),
    []
  );

  const isProposalPackagesCategory =
    !!proposalCategoryFilter &&
    normalizeCategoryKey(proposalCategoryFilter) === proposalPackagesCategoryKey;

  // Filter products/services for proposal builder.
  // Legacy safety: if product_type is blank/unknown, treat as a selectable item.
  const proposalProducts = React.useMemo(() => {
    if (isProposalPackagesCategory) {
      return [];
    }
    let filtered = productsCatalog.filter((p) => {
      const normalizedType = getNormalizedProductType(p);
      return normalizedType !== "monthly_charge" && normalizedType !== "discount";
    });
    // Apply category filter if one is selected
    if (proposalCategoryFilter) {
      const selectedCategory = normalizeCategoryKey(proposalCategoryFilter);
      filtered = filtered.filter(
        (p) => getNormalizedCategory(p) === selectedCategory
      );
    }
    return filtered;
  }, [productsCatalog, proposalCategoryFilter, isProposalPackagesCategory]);

  const proposalLineItemCatalogProducts = React.useMemo(
    () =>
      productsCatalog.filter((p) => {
        const t = getNormalizedProductType(p);
        return t !== "monthly_charge" && t !== "discount";
      }),
    [productsCatalog]
  );

  // Category options for proposal builder from real catalog data.
  const proposalCategories = React.useMemo(() => {
    const byKey = new Map();
    proposalLineItemCatalogProducts.forEach((p) => {
      const label = normalizeCategoryWhitespace(p.category);
      if (!label) return;
      const key = normalizeCategoryKey(label);
      if (!byKey.has(key)) {
        byKey.set(key, label);
      }
    });
    if (packagesCatalog.length > 0) {
      byKey.set(proposalPackagesCategoryKey, "Packages / kits");
    }

    return [...byKey.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [
    proposalLineItemCatalogProducts,
    packagesCatalog.length,
    proposalPackagesCategoryKey,
  ]);




const handleMonthlySelect = (e) => {
  const value = e.target.value;

  // Empty = custom monthly amount / name
  if (!value) {
    setSelectedMonthlyCharge(null);
    return;
  }

  const selected = monthlyChargeProducts.find(
    (p) => String(p.id) === String(value)
  );

  setSelectedMonthlyCharge(selected || null);

  if (selected?.name) {
    setMonthlyServiceLabel(selected.name);
  }
  if (selected?.price) {
    setMonthlyServiceCharge(Number(selected.price));
  }
};

// After catalog loads, re-link draft monthly label to a catalog product for the dropdown.
useEffect(() => {
  if (!monthlyServiceLabel || selectedMonthlyCharge) return;
  const match = monthlyChargeProducts.find(
    (p) => p.name === monthlyServiceLabel
  );
  if (match) setSelectedMonthlyCharge(match);
}, [monthlyChargeProducts, monthlyServiceLabel, selectedMonthlyCharge]);

const handleDiscountSelect = (e) => {
  const value = e.target.value;

  // Empty = custom discount amount
  if (!value) {
    setSelectedDiscount(null);
    setProposalDiscount(0);
    return;
  }

  const selected = discountProducts.find(
    (p) => String(p.id) === String(value)
  );

  setSelectedDiscount(selected || null);
  // Optionally set default amount from discount price if available
  if (selected?.price) {
    setProposalDiscount(Number(selected.price));
  }
};
  const fetchLead = async () => {
    setLoading(true);
    try {
      const res = await API.get(`/leads/${id}/`);
      setLead(res.data);
      // Follow-ups are included in the lead response
      setFollowUps(res.data.followups || []);
      // Initialize contact data
      setContactData({
        first_name: res.data.first_name || "",
        last_name: res.data.last_name || "",
        email: res.data.email || "",
        phone: res.data.phone || "",
        assigned_rep: res.data.assigned_rep?.id || res.data.assigned_rep || "",
        status: res.data.status || "",
        category: res.data.category || "",
        followUpDate: "",
        sms_email_opt_in: res.data.sms_email_opt_in ?? "",
      });
      // Initialize notes
      setNotesText(res.data.notes || "");
      setAdditionalNotesText(res.data.additional_notes || "");
      const proposals = await loadLeadProposalsFromNewApi(id);
      const draftProposal = proposals.find((p) => p.status === "draft");
      if (draftProposal) {
        setActiveProposalId(draftProposal.id);
        setActiveProposalStatus(draftProposal.status || "draft");
        setProposalItems(
          mapNewProposalItemsToRows(
            draftProposal.items || [],
            draftProposal.status || "draft"
          )
        );
        setProposalDiscount(Number(draftProposal.discount_amount || 0));
        setSelectedDiscount(null);
        setMonthlyServiceCharge(
          draftProposal.monthly_service_charge != null
            ? Number(draftProposal.monthly_service_charge)
            : null
        );
        setMonthlyServiceLabel(
          String(draftProposal.monthly_service_label || "").trim()
        );
        setSelectedMonthlyCharge(null);
        setProposalSpecialTerms(String(draftProposal.special_terms || "").trim());
      } else {
        setActiveProposalId(null);
        setActiveProposalStatus("draft");
        setProposalItems([]);
        setProposalDiscount(0);
        setSelectedDiscount(null);
        setMonthlyServiceCharge(null);
        setSelectedMonthlyCharge(null);
        setMonthlyServiceLabel("");
        setProposalSpecialTerms("");
      }
    } catch (err) {
      console.error("Error fetching lead:", err);
      toast.error("Failed to load lead details");
      goBack();
    } finally {
      setLoading(false);
    }
  };

  const ensureDraftProposalForNewFlow = async () => {
    if (!lead?.id) return null;
    if (activeProposalId) return activeProposalId;

    try {
      const res = await API.post(`/leads/${lead.id}/proposals/`, {
        to_email: lead.email || null,
      });
      const created = res?.data;
      setActiveProposalId(created?.id || null);
      setActiveProposalStatus(created?.status || "draft");
      if (created?.id) {
        setLeadProposals((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
      }
      return created?.id || null;
    } catch (err) {
      setActiveProposalId(null);
      setActiveProposalStatus("draft");
      return null;
    }
  };

  const startNewProposalDraft = async () => {
    if (!lead?.id) return;

    try {
      const res = await API.post(`/leads/${lead.id}/proposals/`, {
        to_email: lead.email || null,
      });
      const created = res?.data;
      setActiveProposalId(created?.id || null);
      setActiveProposalStatus(created?.status || "draft");
      if (created?.id) {
        setLeadProposals((prev) => [created, ...prev.filter((p) => p.id !== created.id)]);
      }
      resetProposalBuilderState();
      setShowProposal(true);
    } catch (err) {
      setActiveProposalId(null);
      setActiveProposalStatus("draft");
      toast.error("Unable to create draft proposal.");
    }
  };

  const openProposalBuilder = async () => {
    if (activeProposalId) {
      setShowProposal(true);
      return;
    }
    await startNewProposalDraft();
  };

  const updateProposalItemQuantity = async (itemId, newQty) => {
    if (!activeProposalId) {
      toast.error("No active draft proposal found.");
      return;
    }
    await API.patch(`/proposals/${activeProposalId}/items/${itemId}/`, {
      quantity: newQty,
    });
    setProposalItems((prev) =>
      prev.map((p) =>
        p.id === itemId
          ? { ...p, quantity: newQty }
          : p
      )
    );
  };

  const removeProposalItem = async (itemId) => {
    if (!activeProposalId) {
      toast.error("No active draft proposal found.");
      return;
    }
    await API.delete(`/proposals/${activeProposalId}/items/${itemId}/`);
    setProposalItems((prev) => prev.filter((p) => p.id !== itemId));
  };

  const saveProposalDraft = async () => {
    if (isEditDisabled) return;
    const draftId = await ensureDraftProposalForNewFlow();
    if (!draftId) {
      toast.error("Unable to create draft proposal.");
      return false;
    }
    await API.post(`/proposals/${draftId}/save-draft/`, {
      to_email: lead?.email || null,
      monthly_service_charge: monthlyServiceCharge,
      monthly_service_name:
        selectedMonthlyCharge?.name ||
        monthlyServiceLabel ||
        "Monthly Monitoring Charge",
      proposal_discount: proposalDiscount || 0,
      discount_name: selectedDiscount?.name || "Discount",
      message: "",
      special_terms: proposalSpecialTerms || "",
    });
    toast.success("Draft proposal saved.");
    return true;
  };

  const handleContactEdit = () => {
    setEditingContact(true);
    // Check for existing incomplete follow-up
    const incompleteFollowUp = followUps?.find(f => !f.completed);
    setContactData({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      email: lead.email || "",
      phone: lead.phone || "",
      assigned_rep: lead.assigned_rep?.id || lead.assigned_rep || "",
      status: lead.status || "",
      category: lead.category || "",
      followUpDate: incompleteFollowUp?.due_date ? new Date(incompleteFollowUp.due_date).toISOString().split('T')[0] : "",
      sms_email_opt_in: lead.sms_email_opt_in ?? "",
    });
  };

  const handleContactCancel = () => {
    setEditingContact(false);
    setContactData({
      first_name: lead.first_name || "",
      last_name: lead.last_name || "",
      email: lead.email || "",
      phone: lead.phone || "",
      assigned_rep: lead.assigned_rep?.id || lead.assigned_rep || "",
      status: lead.status || "",
      category: lead.category || "",
      followUpDate: "",
      sms_email_opt_in: lead.sms_email_opt_in ?? "",
    });
  };

  const handleContactSave = async () => {
    if (!lead) return;

    // Validate follow-up date if Follow Up Needed is selected
    if (contactData.status === "Follow Up Needed" && !contactData.followUpDate) {
      toast.error("Please select a follow-up date");
      return;
    }

    try {
      // Extract assigned_rep ID - handle both object, ID, string, and empty string formats
      let assignedRepId = null;
      if (contactData.assigned_rep) {
        if (typeof contactData.assigned_rep === 'object' && contactData.assigned_rep !== null && contactData.assigned_rep.id) {
          assignedRepId = contactData.assigned_rep.id;
        } else if (typeof contactData.assigned_rep === 'number') {
          assignedRepId = contactData.assigned_rep;
        } else if (typeof contactData.assigned_rep === 'string' && contactData.assigned_rep.trim() !== '') {
          const parsed = parseInt(contactData.assigned_rep, 10);
          if (!isNaN(parsed)) {
            assignedRepId = parsed;
          }
        }
      }

      const payload = {
        first_name: (contactData.first_name || "").trim(),
        last_name: (contactData.last_name || "").trim(),
        email: contactData.email || null,
        phone: contactData.phone || null,
        status: contactData.status,
        notes: lead.notes || "",
        assigned_rep: assignedRepId || null,
        territory: lead.territory || null,
        sms_email_opt_in: contactData.sms_email_opt_in || null,
      };
      if (canManageLeadCategory) {
        payload.category = contactData.category ? Number(contactData.category) : null;
      }

      const res = await API.patch(`/leads/${lead.id}/`, payload);
      setLead(res.data);
      setFollowUps(res.data.followups || []);
      emitLeadUpdate(res.data);
      
      // If Follow Up Needed and date provided, create/update follow-up
      if (contactData.status === "Follow Up Needed" && contactData.followUpDate) {
        try {
          // Check for existing incomplete follow-up from response (not stale state)
          const followupsFromResponse = res.data.followups || [];
          const incompleteFollowUp = followupsFromResponse.find(f => !f.completed);
          
          if (incompleteFollowUp) {
            // UPDATE existing follow-up
            const isInitialSchedule = !incompleteFollowUp.due_date;
            await API.patch(`/followups/${incompleteFollowUp.id}/`, {
              due_date: contactData.followUpDate,
              notes: isInitialSchedule
                ? `Follow-up scheduled for ${contactData.followUpDate}`
                : `Follow-up rescheduled to ${contactData.followUpDate}`,
            });
            toast.success("Follow-up date updated!");
          } else {
            // CREATE new follow-up
            await API.post("/followups/", {
              lead: lead.id,
              due_date: contactData.followUpDate,
              completed: false,
              notes: `Follow-up scheduled for ${contactData.followUpDate}`,
            });
            toast.success("Follow-up created!");
          }
          
          // Refresh lead data to get updated follow-ups
          await fetchLead();
        } catch (followUpErr) {
          console.error("Failed to create/update follow-up:", followUpErr);
          // Don't fail the whole save if follow-up operation fails
          toast.warning("Contact updated but failed to update follow-up");
        }
      }
      
      setEditingContact(false);
      toast.success("Contact information updated successfully!");
    } catch (err) {
      console.error("Contact update failed:", err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || "Failed to update contact information";
      toast.error(errorMsg);
    }
  };

  const handleNotesEdit = () => {
    setEditingNotes(true);
    setNotesText(lead.notes || "");
    setMarkFollowUpComplete(false);
  };

  const handleNotesCancel = () => {
    setEditingNotes(false);
    setNotesText(lead.notes || "");
    setMarkFollowUpComplete(false);
  };

  const handleNotesSave = async () => {
    if (!lead) return;

    try {
      const payload = {
        notes: notesText || "",
      };

      const res = await API.patch(`/leads/${lead.id}/`, payload);
      setLead(res.data);
      emitLeadUpdate(res.data);

      if (markFollowUpComplete) {
        const incompleteFollowUp = followUps?.find((f) => !f.completed);
        if (incompleteFollowUp) {
          try {
            await API.patch(`/followups/${incompleteFollowUp.id}/`, { completed: true });
            const refetch = await API.get(`/leads/${lead.id}/`);
            setLead(refetch.data);
            setFollowUps(refetch.data.followups || []);
            emitLeadUpdate(refetch.data);
            refreshCounts();
            toast.success("Notes updated and follow-up marked complete!");
          } catch (followUpErr) {
            console.error("Follow-up complete failed:", followUpErr);
            toast.warning("Notes saved but failed to mark follow-up complete.");
          }
        } else {
          toast.success("Notes updated successfully!");
        }
      } else {
        toast.success("Notes updated successfully!");
      }
    } catch (err) {
      console.error("Notes update failed:", err);
      toast.error("Failed to update notes");
    } finally {
      setEditingNotes(false);
      setMarkFollowUpComplete(false);
    }
  };

  const handleAdditionalNotesEdit = () => {
    setEditingAdditionalNotes(true);
    setAdditionalNotesText(lead.additional_notes || "");
  };

  const handleAdditionalNotesCancel = () => {
    setEditingAdditionalNotes(false);
    setAdditionalNotesText(lead.additional_notes || "");
  };

  const handleAdditionalNotesSave = async () => {
    if (!canViewAdditionalNotes) return;
    if (!lead) return;

    try {
      const payload = {
        additional_notes: additionalNotesText || "",
      };

      const res = await API.patch(`/leads/${lead.id}/`, payload);
      setLead(res.data);
      emitLeadUpdate(res.data);
      setEditingAdditionalNotes(false);
      toast.success("Additional notes updated successfully!");
    } catch (err) {
      console.error("Additional notes update failed:", err);
      toast.error("Failed to update additional notes");
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/leads/${lead.id}/`);
      toast.success("Lead archived successfully");
      goBack();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.detail || "Failed to archive lead");
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleViewOnMap = () => {
    if (lead?.latitude && lead?.longitude) {
      navigate(`/leads?leadId=${lead.id}&lat=${lead.latitude}&lng=${lead.longitude}&zoom=15`);
    } else {
      toast.warning("This lead doesn't have location coordinates");
    }
  };

  const handleRestore = async () => {
    if (!lead?.id) return;
    
    setRestoringLead(true);
    try {
      await API.post(`/leads/${lead.id}/restore/`);
      toast.success("Lead restored successfully!");
      // Refresh the lead data
      await fetchLead();
      setConfirmRestore(false);
    } catch (err) {
      console.error("Restore error:", err);
      const errorMsg = err.response?.data?.detail || "Failed to restore lead";
      toast.error(errorMsg);
    } finally {
      setRestoringLead(false);
    }
  };

  const renderHeaderStatusChip = (status) => {
    const color = getLeadStatusColor(status);
    return (
      <Chip
        label={status || "New"}
        size="small"
        sx={{
          height: 32,
          borderRadius: "999px",
          fontWeight: 800,
          fontSize: "0.78rem",
          letterSpacing: "-0.01em",
          color,
          background: alpha(color, 0.10),
          border: `1px solid ${alpha(color, 0.22)}`,
          boxShadow: `0 1px 0 ${alpha(color, 0.06)} inset`,
          "& .MuiChip-label": {
            px: 1.25,
            py: 0,
          },
        }}
      />
    );
  };

  const isClosedWonLead = (lead?.status || "").toLowerCase() === "closed won";

  const handlePushToFillQuick = async () => {
    if (!isClosedWonLead) {
      toast.info("Only Closed Won leads can be pushed to FillQuick.");
      return;
    }
    if (!fillQuickConfigured) {
      toast.info("FillQuick is not configured in Settings.");
      return;
    }
    try {
      const res = await API.post(`/leads/${id}/push-fillquick/`);
      setLead(res.data);
      toast.success("Lead pushed to FillQuick.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Failed to push lead to FillQuick.";
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (!lead) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Lead not found
          </Typography>
          <Button onClick={goBack} sx={{ mt: 2 }}>
            Back to {fromLabel}
          </Button>
        </Box>
      </Box>
    );
  }

  const fullAddress = [
    lead.address,
    lead.city,
    lead.state,
    lead.zip_code,
  ]
    .filter(Boolean)
    .join(", ");

  const leadBreadcrumbTitle =
    [lead.first_name, lead.last_name].filter(Boolean).join(" ").trim() ||
    lead.company_name ||
    lead.company ||
    (lead.id ? `Lead #${lead.id}` : "Lead");

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 1400, mx: "auto", width: "100%" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Hero Section */}
          <Box
            sx={{
              background: (theme) =>
                theme.palette.mode === "dark"
                  ? "linear-gradient(135deg, rgba(51, 65, 85, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
                  : "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.05) 100%)",
              borderRadius: 3,
              p: { xs: 3, md: 4 },
              border: (theme) =>
                theme.palette.mode === "dark"
                  ? "1px solid rgba(255, 255, 255, 0.1)"
                  : "1px solid rgba(0, 0, 0, 0.08)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0, 0, 0, 0.4)"
                  : "0 4px 20px rgba(0, 0, 0, 0.08)",
            }}
          >
<Stack spacing={2.25}>
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
      gap: 2,
      alignItems: "start",
    }}
  >
    {/* Left side: back button + identity */}
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      {!isMobile && (
        <Breadcrumbs
          aria-label="Lead detail navigation"
          separator=">"
          sx={{ fontSize: "0.8125rem", "& .MuiBreadcrumbs-li": { maxWidth: "100%" } }}
        >
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            color="inherit"
            onClick={goBack}
            sx={{ cursor: "pointer", fontWeight: 600, border: 0, background: "none", p: 0 }}
          >
            {fromLabel}
          </Link>
          <Typography variant="body2" color="text.primary" noWrap sx={{ maxWidth: 360 }}>
            {leadBreadcrumbTitle}
          </Typography>
        </Breadcrumbs>
      )}
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={goBack}
          sx={headerGhostButtonSx}
        >
          Back to {fromLabel}
        </Button>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="h4"
          fontWeight={700}
          sx={{
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.82) 100%)"
                : "linear-gradient(135deg, #1e293b 0%, #475569 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontSize: { xs: "1.55rem", sm: "1.8rem", md: "2.125rem" },
            lineHeight: 1.08,
            letterSpacing: "-0.02em",
          }}
        >
          {lead.first_name} {lead.last_name}
        </Typography>

        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          flexWrap="wrap"
          sx={{ mt: 1.25 }}
        >
          <Typography variant="body2" color="text.secondary">
            Lead ID: {lead.id}
          </Typography>

          {/* Status Display (edit in Contact Information card) */}
          {renderHeaderStatusChip(lead.status)}

          {lead.is_archived && (
            <Chip
              icon={<ArchiveIcon />}
              label="Archived"
              size="small"
              color="warning"
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>
      </Box>
    </Stack>

    {/* Right side / mobile second row: action buttons */}
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.25}
      sx={{
        width: { xs: "100%", md: "auto" },
        alignSelf: { xs: "stretch", md: "start" },
      }}
    >
      {/* Action buttons - only show for active (non-archived) leads */}
      {!lead.is_archived && (
        <>
          {isClosedWonLead && (
            lead.fillquick_lead_id ? (
              <Tooltip
                title={
                  lead.fillquick_pushed_at
                    ? `Pushed at ${new Date(lead.fillquick_pushed_at).toLocaleString()}`
                    : "Pushed to FillQuick"
                }
              >
                <Chip
                  icon={<CheckCircleIcon />}
                  label="In FillQuick"
                  color="success"
                  sx={{
                    height: 42,
                    borderRadius: "14px",
                    fontWeight: 800,
                    fontSize: "0.82rem",
                  }}
                />
              </Tooltip>
            ) : (
              <Tooltip title={fillQuickConfigured ? "Create lead in FillQuick" : "Configure FillQuick in Settings first"}>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    onClick={handlePushToFillQuick}
                    disabled={!fillQuickConfigured}
                    fullWidth={isMobile}
                    sx={headerOutlineButtonSx}
                  >
                    Push to FillQuick
                  </Button>
                </span>
              </Tooltip>
            )
          )}
          <Button
            data-tour="lead-detail-view-on-map"
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={handleViewOnMap}
            disabled={!lead.latitude || !lead.longitude}
            fullWidth={isMobile}
            sx={headerOutlineButtonSx}
          >
            View on Map
          </Button>

          <Button
            variant="contained"
            startIcon={<DescriptionIcon />}
            onClick={openProposalBuilder}
            disabled={!canEditLead}
            fullWidth={isMobile}
            sx={headerPrimaryButtonSx}
          >
            {activeProposalId ? "Open Draft Proposal" : "Create Proposal"}
          </Button>
          {canDeleteLead && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setConfirmDelete(true)}
              fullWidth={isMobile}
              sx={headerOutlineButtonSx}
            >
              Delete Lead
            </Button>
          )}
        </>
      )}

      {/* Show Restore button if lead is archived and user is Admin/Sales Director */}
      {lead.is_archived && (role === "Admin" || role === "Sales Director") && (
        <Button
          variant="contained"
          color="success"
          startIcon={<RestoreIcon />}
          onClick={() => setConfirmRestore(true)}
          disabled={isEditDisabled || restoringLead}
          fullWidth={isMobile}
          sx={{
            ...headerPrimaryButtonSx,
            background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
            boxShadow: "0 10px 22px rgba(22,163,74,0.24)",
            "&:hover": {
              background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
              boxShadow: "0 14px 28px rgba(22,163,74,0.34)",
              transform: "translateY(-1px)",
            },
          }}
        >
          {restoringLead ? "Restoring..." : "Restore Lead"}
        </Button>
      )}
    </Stack>
  </Box>
</Stack>
          </Box>

        {/* ------------------------------------------------------------------Main Grid - 3 Column Layout */}
        <Box
          display="grid"
          gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
          gap={3}
          sx={{
            order: { xs: 2, md: 1 }
          }}
        >
          {/* -----------------------------------------------------First Card - Contact Information */}
          <Card
            data-tour="lead-detail-contact-info"
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent
              sx={{
                fontFamily: `"Inter", "Manrope", "Plus Jakarta Sans", "Segoe UI", system-ui, sans-serif`,
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography fontWeight={700}>
                  Contact Information
                </Typography>
                {editingContact ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleContactSave}
                      disabled={isEditDisabled || !canEditLead}
                      sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
                    >
                      Save
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleContactCancel}
                      sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5, minWidth: "auto" }}
                    >
                      Cancel
                    </Button>
                  </Stack>
                ) : (
                  <IconButton
                    data-tour="lead-detail-contact-edit"
                    size="small"
                    onClick={handleContactEdit}
                    disabled={isEditDisabled || !canEditLead}
                    sx={{
                      p: 0.5,
                      color: "primary.main",
                      opacity: isEditDisabled ? 0.3 : 1,
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Name
                  </Typography>
                  {editingContact ? (
                    <Stack direction="row" spacing={1}>
                      <TextField
                        value={contactData.first_name}
                        onChange={(e) => setContactData({ ...contactData, first_name: e.target.value })}
                        size="small"
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        placeholder="First name"
                        disabled={isEditDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": { fontSize: "0.875rem", height: "auto" },
                          "& .MuiInputBase-input": { py: 0.5 },
                        }}
                      />
                      <TextField
                        value={contactData.last_name}
                        onChange={(e) => setContactData({ ...contactData, last_name: e.target.value })}
                        size="small"
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        placeholder="Last name"
                        disabled={isEditDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": { fontSize: "0.875rem", height: "auto" },
                          "& .MuiInputBase-input": { py: 0.5 },
                        }}
                      />
                    </Stack>
                  ) : (
                    <Typography variant="body2">
                      {lead.first_name || lead.last_name ? [lead.first_name, lead.last_name].filter(Boolean).join(" ") : "—"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Email
                  </Typography>
                  {editingContact ? (
                    <TextField
                      type="email"
                      value={contactData.email}
                      onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                      size="small"
                      margin="dense"
                      fullWidth
                      variant="outlined"
                      disabled={isEditDisabled}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.875rem", // Match body2 font size
                          height: "auto",
                        },
                        "& .MuiInputBase-input": {
                          py: 0.5,
                        },
                      }}
                    />
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <EmailIcon sx={{ color: "primary.main" }} fontSize="small" />
                      <Typography variant="body2">
                        {lead.email || "—"}
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Phone
                  </Typography>
                  {editingContact ? (
                    <TextField
                      type="tel"
                      value={contactData.phone}
                      onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
                      size="small"
                      margin="dense"
                      fullWidth
                      variant="outlined"
                      disabled={isEditDisabled}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.875rem", // Match body2 font size
                          height: "auto",
                        },
                        "& .MuiInputBase-input": {
                          py: 0.5,
                        },
                      }}
                    />
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIcon sx={{ color: "success.main" }} fontSize="small" />
                      <Typography variant="body2">
                        {lead.phone || "—"}
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    SMS/Email Opt-In
                  </Typography>
                  {editingContact ? (
                    <FormControl fullWidth size="small" margin="dense" disabled={isEditDisabled || !contactData.phone}>
                      <Select
                        value={contactData.sms_email_opt_in ?? ""}
                        onChange={(e) => setContactData({ ...contactData, sms_email_opt_in: e.target.value })}
                        displayEmpty
                        sx={{ fontSize: "0.875rem" }}
                      >
                        <MenuItem value=""><em>—</em></MenuItem>
                        <MenuItem value="yes">Yes</MenuItem>
                        <MenuItem value="no">No</MenuItem>
                      </Select>
                      {!contactData.phone && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Enter a phone number to set opt-in status
                        </Typography>
                      )}
                    </FormControl>
                  ) : (
                    <Typography variant="body2">
                      {lead.sms_email_opt_in === "yes" ? "Yes" : lead.sms_email_opt_in === "no" ? "No" : "—"}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Address
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <LocationOnIcon sx={{ color: "error.main", mt: 0.5 }} fontSize="small" />
                    <Typography variant="body2">
                      {fullAddress || "—"}
                    </Typography>
                  </Stack>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Status
                  </Typography>
                  {editingContact ? (
                    <>
                      <FormControl fullWidth size="small" margin="dense">
                        <Select
                          value={contactData.status || ""}
                          onChange={(e) => setContactData({ ...contactData, status: e.target.value })}
                          disabled={isEditDisabled}
                          renderValue={(selected) => {
                            const option = STATUS_OPTIONS.find((o) => o.value === selected);
                            if (!option) return selected;
                            return (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <LeadStatusIcon status={option.value} size={18} />
                                <Typography
                                  sx={{
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    color: "text.primary",
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {option.label || option.value}
                                </Typography>
                              </Box>
                            );
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                mt: 0.5,
                                borderRadius: "14px",
                                border: "1px solid rgba(15,23,42,0.08)",
                                boxShadow: "0 12px 32px rgba(15,23,42,0.12)",
                                maxHeight: 420,
                              },
                            },
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              fontSize: "0.875rem",
                              height: "auto",
                            },
                            "& .MuiInputBase-input": {
                              py: 0.5,
                            },
                          }}
                        >
                          {STATUS_OPTIONS.map((option) => {
                            const color = getLeadStatusColor(option.value);
                            const isSelected = contactData.status === option.value;

                            return (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                  px: 0.75,
                                  py: 0.5,
                                  mx: 0.5,
                                  my: 0.25,
                                  borderRadius: "10px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    width: "100%",
                                    px: 1.2,
                                    py: 0.7,
                                    borderRadius: "12px",
                                    background: isSelected
                                      ? alpha(color, 0.08)
                                      : "background.paper",
                                    border: `1px solid ${
                                      isSelected
                                        ? alpha(color, 0.35)
                                        : "rgba(15,23,42,0.08)"
                                    }`,
                                    boxShadow: isSelected
                                      ? `0 0 0 1px ${alpha(color, 0.1)}`
                                      : "none",
                                    transition: "all 0.18s ease",
                                    "&:hover": {
                                      background: isSelected
                                        ? alpha(color, 0.1)
                                        : "rgba(248,250,252,0.95)",
                                    },
                                  }}
                                >
                                  <LeadStatusIcon status={option.value} size={18} color={color} />
                                  <Typography
                                    sx={{
                                      fontSize: "0.85rem",
                                      fontWeight: isSelected ? 800 : 700,
                                      color: "text.primary",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {option.label || option.value}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                      
                      {/* ---------------------------------------------------Conditional Follow-up Date Picker */}
                      {contactData.status === "Follow Up Needed" && (
                        <TextField
                          label="Follow-Up Date"
                          type="date"
                          size="small"
                          fullWidth
                          margin="dense"
                          value={contactData.followUpDate}
                          onChange={(e) => setContactData({ ...contactData, followUpDate: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                          required
                          disabled={isEditDisabled}
                          helperText={
                            followUps?.find(f => !f.completed)
                              ? "Updating existing follow-up date"
                              : "Creating new follow-up"
                          }
                          sx={{
                            mt: 1,
                            "& .MuiOutlinedInput-root": {
                              fontSize: "0.875rem",
                            },
                            "& .MuiInputBase-input": {
                              py: 0.5,
                            },
                          }}
                        />
                      )}
                    </>
                  ) : (
                  renderHeaderStatusChip(lead.status)
                  )}
                </Box>
                {canManageLeadCategory && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                      Category
                    </Typography>
                    {editingContact ? (
                      <FormControl fullWidth size="small" margin="dense" disabled={isEditDisabled}>
                        <Select
                          value={contactData.category || ""}
                          onChange={(e) => setContactData({ ...contactData, category: e.target.value })}
                          displayEmpty
                          disabled={isEditDisabled}
                          sx={{ fontSize: "0.875rem" }}
                        >
                          <MenuItem value="">
                            <em>Uncategorized</em>
                          </MenuItem>
                          {leadCategories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {displayLeadCategoryLabel(cat.label)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={displayLeadCategoryLabel(lead.category_label)}
                        sx={leadCategoryChipSx(getLeadCategoryOutlineColor(lead))}
                      />
                    )}
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Assigned Rep
                  </Typography>
                  {editingContact ? (
                    <FormControl fullWidth size="small" margin="dense" disabled={isEditDisabled || role === "Sales Rep"}>
                      <InputLabel>Assigned Rep</InputLabel>
                      <Select
                        value={contactData.assigned_rep || ""}
                        onChange={(e) => setContactData({ ...contactData, assigned_rep: e.target.value })}
                        label="Assigned Rep"
                        disabled={isEditDisabled || role === "Sales Rep"}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: "0.875rem",
                            height: "auto",
                          },
                          "& .MuiInputBase-input": {
                            py: 0.5,
                          },
                        }}
                      >
                        <MenuItem value="">
                          <em>Unassigned</em>
                        </MenuItem>
                        {buildAssignedRepOptions({
                          users,
                          currentRepId: contactData.assigned_rep,
                          currentRepName: lead.assigned_rep_name,
                        }).map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {formatAssignedRepOptionLabel(user)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonIcon color="action" fontSize="small" />
                      <Typography variant="body2">
                        {lead.assigned_rep_name || "Unassigned"}
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Territory
                  </Typography>
                  <Typography variant="body2">
                    {lead.territory_name || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {lead.created_at
                      ? new Date(lead.created_at).toLocaleString()
                      : "—"}
                  </Typography>
                </Box>
                {lead.latitude && lead.longitude && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Location Coordinates
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">
                        Lat: {lead.latitude}, Lng: {lead.longitude}
                      </Typography>
                    </Box>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>

          {/* -----------------------------------------------------------Second Card - Property Details */}
          <Card
            data-tour="lead-detail-property-details"
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              <Typography fontWeight={700} mb={2}>
                Property Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {lead.property_type ||
              lead.sold_date ||
              lead.price ||
              lead.square_feet ||
              lead.year_built ? (
                <Stack spacing={2}>
                  {lead.property_type && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Property Type
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <HomeIcon color="action" fontSize="small" />
                        <Typography variant="body2">
                          {lead.property_type}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  {lead.sold_date && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Sold Date
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarTodayIcon color="action" fontSize="small" />
                        <Typography variant="body2">
                          {new Date(lead.sold_date).toLocaleDateString()}
                        </Typography>
                      </Stack>
                    </Box>
                  )}
                  {lead.price && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Price
                      </Typography>
                      <Typography variant="body2">
                        ${parseFloat(lead.price).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                  {lead.square_feet && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Square Feet
                      </Typography>
                      <Typography variant="body2">
                        {parseFloat(lead.square_feet).toLocaleString()} sq ft
                      </Typography>
                    </Box>
                  )}
                  {lead.year_built && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Year Built
                      </Typography>
                      <Typography variant="body2">{lead.year_built}</Typography>
                    </Box>
                  )}
                  {/* Additional Notes in Property Details Card */}
                  {canViewAdditionalNotes &&
                    (editingAdditionalNotes ? (
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                          Additional Notes
                        </Typography>
                        <TextField
                          multiline
                          rows={4}
                          value={additionalNotesText}
                          onChange={(e) => setAdditionalNotesText(e.target.value)}
                          size="small"
                          margin="dense"
                          fullWidth
                          variant="outlined"
                          placeholder="Add additional notes..."
                          disabled={isEditDisabled}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              fontSize: "0.875rem",
                            },
                            "& .MuiInputBase-input": {
                              py: 0.75,
                            },
                          }}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleAdditionalNotesSave}
                            disabled={isEditDisabled || !canEditLead}
                            sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5 }}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleAdditionalNotesCancel}
                            sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5 }}
                          >
                            Cancel
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      <Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            Additional Notes
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={handleAdditionalNotesEdit}
                            disabled={isEditDisabled || !canEditLead}
                            sx={{
                              p: 0.25,
                              color: "primary.main",
                              mt: -0.5,
                              opacity: isEditDisabled ? 0.3 : 1,
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                          {lead.additional_notes || "--"}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No property details available.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* ----------------------------------------------------Third Card - Notes & Follow-ups */}
          <Card
            data-tour="lead-detail-notes-followups"
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography fontWeight={700}>
                  Notes & Follow-ups
                </Typography>
                <IconButton
  size="small"
  onClick={editingNotes ? handleNotesCancel : handleNotesEdit}
  disabled={isEditDisabled || !canEditLead}
  sx={{
    p: 0.5,
    color: "primary.main",
    opacity: isEditDisabled ? 0.3 : 1,
  }}
>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                {/*------------------------------------------------------------------ Notes Section */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                    NOTES
                  </Typography>
                  {editingNotes ? (
                    <>
                      <TextField
                        multiline
                        rows={4}
                        value={notesText}
                        onChange={(e) => setNotesText(e.target.value)}
                        size="small"
                        margin="dense"
                        fullWidth
                        variant="outlined"
                        placeholder="Add notes..."
                        disabled={isEditDisabled}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            fontSize: "0.875rem", // Match body2 font size
                          },
                          "& .MuiInputBase-input": {
                            py: 0.75,
                          },
                        }}
                      />
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mt: 1 }}
                        flexWrap="wrap"
                      >
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleNotesSave}
                          disabled={isEditDisabled || !canEditLead}
                          sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5 }}
                        >
                          Save
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={handleNotesCancel}
                          sx={{ fontSize: "0.75rem", px: 1.5, py: 0.5 }}
                        >
                          Cancel
                        </Button>
                        {followUps?.find((f) => !f.completed) && (
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={markFollowUpComplete}
                                onChange={(e) => setMarkFollowUpComplete(e.target.checked)}
                                size="small"
                              />
                            }
                            label="Mark follow-up completed"
                            sx={{
                              ml: 1,
                              "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                            }}
                          />
                        )}
                      </Stack>
                    </>
                  ) : (
                    lead.notes ? (
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {lead.notes}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No notes available.
                      </Typography>
                    )
                  )}
                </Box>

                <Divider />

                {/* -------------------------------------------------------------Follow-ups Section */}
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                    FOLLOW-UPS ({followUps.length})
                  </Typography>
                  {followUps.length > 0 ? (
                    <Stack spacing={1.5}>
                      {followUps.map((followUp) => (
                        <Box
                          key={followUp.id}
                          sx={{
                            p: 1.5,
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            bgcolor: followUp.completed
                              ? "action.hover"
                              : "background.paper",
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              {followUp.title && (
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                                  {followUp.title.replace(/^Follow Up: Follow Up for /, "Follow-up for ")}
                                </Typography>
                              )}
                              {followUp.due_date && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                  Due: {new Date(followUp.due_date).toLocaleDateString()}
                                </Typography>
                              )}
                              {followUp.notes && (
                                <Typography variant="body2" sx={{ mt: 0.5, fontSize: "0.75rem" }}>
                                  {followUp.notes}
                                </Typography>
                              )}
                              {followUp.created_by_name && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                  by {followUp.created_by_name}
                                </Typography>
                              )}
                            </Box>
                            <Chip
                              label={followUp.completed ? "Done" : "Pending"}
                              size="small"
                              color={followUp.completed ? "success" : "warning"}
                              sx={{ ml: 1, flexShrink: 0 }}
                            />
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No follow-ups for this lead.
                    </Typography>
                  )}
                </Box>

                {/* ----------------------------------------------------------Activity Timeline */}
                {token && (
                  <>
                    <Divider />
                    <Box data-tour="lead-detail-activity-timeline">
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                        ACTIVITY TIMELINE
                      </Typography>
                      <ActivityTimeline token={token} leadId={lead.id} />
                    </Box>
                  </>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* ----------------------------------------------Proposal History */}
        {proposalHistory.length > 0 && (
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(30,41,59,0.88)"
                  : "background.paper",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.08)"
                  : "rgba(0,0,0,0.06)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 8px 24px rgba(0,0,0,0.55)"
                  : "0 4px 12px rgba(0,0,0,0.12)",
              mt: 3,
            }}
          >
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => setShowProposalSummary((v) => !v)}
              >
                <Typography fontWeight={700}>
                  Proposal History ({proposalHistory.length})
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {showProposalSummary ? "Hide" : "Show"}
                </Typography>
              </Box>

              {showProposalSummary && (
  <>
    <Divider sx={{ my: 2 }} />

    {proposalHistory.length === 0 ? (
      <Typography variant="body2" sx={{ opacity: 0.6 }}>
        No proposals sent yet.
      </Typography>
    ) : (
      isMobile ? renderMobileRows() : renderDesktopRows()
    )}
  </>
)}
            </CardContent>
          </Card>
        )}



        {/* -------------------------------------------------------------------Proposal Builder */}
        {showProposal && (
          <Card
            sx={{
              borderRadius: 3,
              bgcolor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(15,23,42,0.92)"
                  : "rgba(255,255,255,0.92)",
              border: "1px solid",
              borderColor: (theme) =>
                theme.palette.mode === "dark"
                  ? "rgba(59,130,246,0.24)"
                  : "rgba(37,99,235,0.16)",
              boxShadow: (theme) =>
                theme.palette.mode === "dark"
                  ? "0 16px 40px rgba(2,6,23,0.55)"
                  : "0 14px 34px rgba(15,23,42,0.12)",
              mt: 3,
              order: { xs: 1, md: 2 },
              backdropFilter: "blur(8px)",
            }}
          >
            <CardContent>
              {/* ---------------------------------------------------Header */}
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: { xs: "wrap", sm: "nowrap" }, gap: { xs: 1, sm: 0 } }}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{ fontSize: { xs: "0.85rem", sm: "0.9rem" }, fontWeight: 700 }}
                  >
                    {activeProposalId ? `Proposal #${activeProposalId}` : "Create Proposal"}
                  </Typography>
                  <Chip
                    label={proposalStatus.charAt(0).toUpperCase() + proposalStatus.slice(1)}
                    size="small"
                    color={
                      proposalStatus === "accepted"
                        ? "success"
                        : proposalStatus === "sent"
                        ? "info"
                        : proposalStatus === "withdrawn"
                        ? "warning"
                        : "default"
                    }
                    sx={{ fontWeight: 600 }}
                  />
                  {activeProposalStatus === "draft" && (
                    <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }}>
                      Editable draft
                    </Typography>
                  )}
                </Stack>
                <IconButton
                  size="small"
                  onClick={() => setShowProposal(false)}
                  sx={{
                    p: 0.5,
                    color: "text.secondary",
                    flexShrink: 0,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Divider sx={{ mb: 3 }} />

              {/* ----------------------------------------------------------------Current Proposal Items */}
              {isMobile ? (
                <Box sx={{ mb: 3 }}>
                  {proposalItems.length > 0 ? (
                    <Stack spacing={1.25}>
                      {proposalItems.map((item) => (
                        <Box
                          key={item.id}
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "divider",
                            bgcolor: (theme) =>
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.03)"
                                : "rgba(255,255,255,0.96)",
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" spacing={1}>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, lineHeight: 1.25 }}>
                                {item.product_name || "Unknown Product"}
                              </Typography>
                              <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.25 }}>
                                SKU: {item.product?.sku || "—"}
                              </Typography>
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isEditDisabled}
                              onClick={async () => {
                                if (isEditDisabled) return;
                                try {
                                  await removeProposalItem(item.id);
                                  toast.success("Product removed from proposal");
                                } catch (err) {
                                  console.error("Error removing product:", err);
                                  toast.error("Failed to remove product");
                                }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>

                          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
                            <Box sx={{ width: 104 }}>
                              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", mb: 0.5 }}>
                                Quantity
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={async (e) => {
                                  if (isEditDisabled) return;
                                  try {
                                    const newQty = parseInt(e.target.value, 10) || 1;
                                    await updateProposalItemQuantity(item.id, newQty);
                                  } catch (err) {
                                    console.error("Error updating quantity:", err);
                                    toast.error("Failed to update quantity");
                                  }
                                }}
                                disabled={isEditDisabled}
                                inputProps={{ min: 1, style: { textAlign: "center" } }}
                                sx={{
                                  ...proposalTextFieldSx,
                                  width: "100%",
                                  "& .MuiInputBase-input": {
                                    fontSize: "0.9rem",
                                    fontWeight: 500,
                                    py: 1,
                                    textAlign: "center",
                                  },
                                }}
                              />
                            </Box>

                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", mb: 0.5 }}>
                                Unit Price
                              </Typography>
                              <Box
                                sx={{
                                  minHeight: 46,
                                  borderRadius: "12px",
                                  border: "1px solid",
                                  borderColor: "divider",
                                  display: "flex",
                                  alignItems: "center",
                                  px: 1.5,
                                  fontWeight: 600,
                                  fontSize: "0.9rem",
                                }}
                              >
                                ${parseFloat(item.unit_price || 0).toFixed(2)}
                              </Box>
                            </Box>
                          </Stack>

                          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.25 }}>
                            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                              Line total
                            </Typography>
                            <Typography sx={{ fontSize: "0.9rem", fontWeight: 800 }}>
                              ${(parseFloat(item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}
                            </Typography>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Box sx={{ mb: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        No products added to proposal yet. Use the form below to add products or services.
                      </Typography>
                    </Box>
                  )}

                  <Box
                    sx={{
                      mt: proposalItems.length > 0 ? 1.25 : 0,
                      p: 1.5,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.02)"
                          : "rgba(15,23,42,0.02)",
                    }}
                  >
                    {proposalDiscount > 0 && (
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                          Discount
                        </Typography>
                        <Typography sx={{ fontSize: "0.83rem", color: "error.main", fontWeight: 700 }}>
                          -${proposalDiscount.toFixed(2)}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                      <Typography sx={{ fontSize: "0.9rem", fontWeight: 700 }}>
                        Total (one-time)
                      </Typography>
                      <Typography sx={{ fontSize: "0.98rem", fontWeight: 800 }}>
                        ${proposalTotal.toFixed(2)}
                      </Typography>
                    </Stack>

                    {showProposalMonthlySummaryLine && (
                      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.75 }}>
                        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                          {proposalMonthlyLineLabel}
                        </Typography>
                        <Typography sx={{ fontSize: "0.83rem", fontWeight: 700 }} color="primary">
                          {proposalMonthlyLineAmountText}
                        </Typography>
                      </Stack>
                    )}
                  </Box>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    mb: 3,
                    borderRadius: 2.5,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "none",
                    overflowX: "auto",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product/Service</TableCell>
                        <TableCell>SKU</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {proposalItems.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 2.5, color: "text.secondary" }}>
                            <Typography variant="body2">
                              No products added yet. Use the form below to add products or services.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        proposalItems.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.product_name || "Unknown Product"}</TableCell>
                            <TableCell>{item.product?.sku || "—"}</TableCell>
                            <TableCell align="right">
                              <TextField
                                type="number"
                                size="small"
                                value={item.quantity}
                                onChange={async (e) => {
                                  if (isEditDisabled) return;
                                  try {
                                    const newQty = parseInt(e.target.value, 10) || 1;
                                    await updateProposalItemQuantity(item.id, newQty);
                                  } catch (err) {
                                    console.error("Error updating quantity:", err);
                                    toast.error("Failed to update quantity");
                                  }
                                }}
                                disabled={isEditDisabled}
                                inputProps={{ min: 1, style: { textAlign: "center" } }}
                                sx={{
                                  ...proposalTextFieldSx,
                                  width: 80,
                                  "& .MuiInputBase-input": {
                                    fontSize: "0.9rem",
                                    fontWeight: 500,
                                    py: 1,
                                    textAlign: "center",
                                  },
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              ${parseFloat(item.unit_price || 0).toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              ${(parseFloat(item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                disabled={isEditDisabled}
                                onClick={async () => {
                                  if (isEditDisabled) return;
                                  try {
                                    await removeProposalItem(item.id);
                                    toast.success("Product removed from proposal");
                                  } catch (err) {
                                    console.error("Error removing product:", err);
                                    toast.error("Failed to remove product");
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                      {proposalDiscount > 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="right">
                            <Typography variant="body2" color="text.secondary">
                              Discount
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="error">
                              −${proposalDiscount.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      )}

                      <TableRow>
                        <TableCell colSpan={4} align="right">
                          <Typography variant="subtitle1" fontWeight={700}>
                            Total (one-time):
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="subtitle1" fontWeight={700}>
                            ${proposalTotal.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell />
                      </TableRow>

                      {showProposalMonthlySummaryLine && (
                        <TableRow>
                          <TableCell colSpan={4} align="right">
                            <Typography variant="body2" color="text.secondary">
                              {proposalMonthlyLineLabel}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="primary" fontWeight={600}>
                              {proposalMonthlyLineAmountText}
                            </Typography>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}


              {/* -----------------------------------------------------------------Add Product Section */}
             <Typography
  sx={{
    fontSize: "0.8rem",
    fontWeight: 600,
    mb: 1,
  }}
>
  {isProposalPackagesCategory ? "Add package to proposal" : "Add Product/Service"}
</Typography>

<Box
  sx={{
    p: 2,
    bgcolor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(37,99,235,0.10)"
        : "rgba(37,99,235,0.04)",
    border: "1px solid",
    borderColor: (theme) =>
      theme.palette.mode === "dark"
        ? "rgba(59,130,246,0.24)"
        : "rgba(59,130,246,0.2)",
    borderRadius: 2.5,
  }}
>
  <Stack
    direction={{ xs: "column", sm: "row" }}
    spacing={2}
    alignItems={{ xs: "stretch", sm: "center" }}
  >
    {/* Category Filter Dropdown */}
    <FormControl
      size="small"
      sx={{ 
        width: { xs: "100%", sm: "auto" },
        minWidth: { xs: "100%", sm: 200 },
        maxWidth: { xs: "100%", sm: 250 },
      }}
    >
      <InputLabel>Category</InputLabel>
      <Select
        value={proposalCategoryFilter}
        onChange={(e) => setProposalCategoryFilter(e.target.value)}
        label="Category"
        disabled={isEditDisabled}
        sx={proposalSelectSx}
        MenuProps={{
          ...proposalMenuProps,
          PaperProps: {
            ...proposalMenuProps.PaperProps,
            sx: {
              ...proposalMenuProps.PaperProps.sx,
              maxHeight: 280,
            },
          },
        }}
      >
        <MenuItem value="">
          <em>All Categories</em>
        </MenuItem>
        {proposalCategories.map((category) => (
          <MenuItem key={category.value} value={category.value}>
            {category.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>

    <FormControl
      size="small"
      sx={{ 
        width: { xs: "100%", sm: "auto" },
        minWidth: { xs: "100%", sm: 520 },
        maxWidth: { xs: "100%", sm: 640 },
      }}
    >
      <InputLabel>
        {isProposalPackagesCategory ? "Select package / kit" : "Select Product/Service"}
      </InputLabel>
      <Select
        value={isProposalPackagesCategory ? addingPackageId : addingProductId}
        onChange={(e) =>
          isProposalPackagesCategory
            ? setAddingPackageId(e.target.value)
            : setAddingProductId(e.target.value)
        }
        label={
          isProposalPackagesCategory ? "Select package / kit" : "Select Product/Service"
        }
        disabled={isEditDisabled}
        sx={proposalSelectSx}
        MenuProps={{
          ...proposalMenuProps,
          PaperProps: {
            ...proposalMenuProps.PaperProps,
            sx: {
              ...proposalMenuProps.PaperProps.sx,
              maxHeight: 320,
            },
          },
        }}
      >
        <MenuItem value="">
          <em>Choose...</em>
        </MenuItem>
        {isProposalPackagesCategory
          ? packagesCatalog.map((pkg) => (
              <MenuItem key={pkg.id} value={String(pkg.id)}>
                {pkg.name} ({(pkg.lines || []).length} line
                {(pkg.lines || []).length === 1 ? "" : "s"})
              </MenuItem>
            ))
          : proposalProducts.map((product) => (
              <MenuItem key={product.id} value={product.id}>
                {product.name} — ${parseFloat(product.price || 0).toFixed(2)}
                {product.sku && ` (${product.sku})`}
              </MenuItem>
            ))}
      </Select>
    </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    disabled={
                      isEditDisabled ||
                      (isProposalPackagesCategory
                        ? !addingPackageId
                        : !addingProductId)
                    }
                    fullWidth={isMobile}
                    sx={{
                      minHeight: 40,
                      borderRadius: "12px",
                      px: 2.2,
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "0 8px 20px rgba(37,99,235,0.28)",
                    }}
                    onClick={async () => {
                      if (isEditDisabled) return;
                      try {
                        const draftId = await ensureDraftProposalForNewFlow();
                        if (!draftId) {
                          toast.error("Unable to create draft proposal.");
                          return;
                        }

                        if (isProposalPackagesCategory) {
                          const pkg = packagesCatalog.find(
                            (p) => String(p.id) === String(addingPackageId)
                          );
                          if (!pkg) {
                            toast.error("Package not found");
                            return;
                          }
                          const response = await API.post(
                            `/proposals/${draftId}/add-package/`,
                            { product_package: pkg.id }
                          );
                          const created = Array.isArray(response.data)
                            ? response.data
                            : [];
                          if (!created.length) {
                            toast.error("No lines were added from this package.");
                            return;
                          }
                          setProposalItems((prev) => {
                            const next = [...prev];
                            for (const newItem of created) {
                              const pid = newItem.product;
                              const catProduct = productsCatalog.find(
                                (p) => p.id === pid
                              );
                              next.push({
                                id: newItem.id,
                                product: {
                                  id: pid,
                                  name: catProduct?.name,
                                  sku:
                                    newItem.sku_snapshot || catProduct?.sku || "",
                                },
                                product_name:
                                  newItem.name_snapshot ||
                                  catProduct?.name ||
                                  "Unknown Product",
                                quantity: newItem.quantity || 1,
                                unit_price:
                                  newItem.unit_price_snapshot ??
                                  catProduct?.price ??
                                  0,
                                status: activeProposalStatus || "draft",
                              });
                            }
                            return next;
                          });
                          setAddingPackageId("");
                          toast.success(
                            `Added ${created.length} line${
                              created.length === 1 ? "" : "s"
                            } from package`
                          );
                          return;
                        }

                        const selectedProduct = proposalProducts.find(
                          (p) => p.id === parseInt(addingProductId, 10)
                        );

                        if (!selectedProduct) {
                          toast.error("Product not found");
                          return;
                        }

                        const existing = proposalItems.find(
                          (p) =>
                            p.product === selectedProduct.id ||
                            p.product?.id === selectedProduct.id
                        );

                        if (existing) {
                          toast.warning("Product already in proposal");
                          return;
                        }

                        const response = await API.post(`/proposals/${draftId}/items/`, {
                          product: selectedProduct.id,
                          quantity: 1,
                          unit_price: selectedProduct.price || "0.00",
                        });
                        const newItem = response.data;
                        const formattedProduct = {
                          id: newItem.id,
                          product: {
                            id: selectedProduct.id,
                            name: selectedProduct.name,
                            sku: newItem.sku_snapshot || selectedProduct.sku,
                            price: selectedProduct.price,
                          },
                          product_name:
                            newItem.name_snapshot || selectedProduct.name,
                          quantity: newItem.quantity || 1,
                          unit_price:
                            newItem.unit_price_snapshot || selectedProduct.price,
                          status: activeProposalStatus || "draft",
                        };

                        setProposalItems((prev) => [...prev, formattedProduct]);
                        setAddingProductId("");
                        toast.success("Product added to proposal");
                      } catch (err) {
                        console.error("Error adding to proposal:", err);
                        const errorMsg =
                          err.response?.data?.detail ||
                          err.response?.data?.error ||
                          JSON.stringify(err.response?.data) ||
                          "Failed to add to proposal";
                        toast.error(errorMsg);
                      }
                    }}
>
  Add
</Button>

                </Stack>
              </Box>

{/* -------------------- Discounts (one-time) */}
<Box sx={{ mt: 2 }}>
  <Typography
    sx={{
      fontSize: "0.8rem",
      fontWeight: 600,
      mb: 1,
    }}
  >
    Discounts
  </Typography>

  <Box
    sx={{
      p: 2,
      border: "1px solid",
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(16,185,129,0.24)"
          : "rgba(16,185,129,0.2)",
      bgcolor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(16,185,129,0.08)"
          : "rgba(16,185,129,0.04)",
      borderRadius: 2.5,
    }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      {/* Discount Selector */}
      <FormControl
        size="small"
        sx={{
          width: { xs: "100%", sm: "auto" },
          minWidth: { xs: "100%", sm: 520 },
          maxWidth: { xs: "100%", sm: 640 },
        }}
      >
        <InputLabel>Select Discount Type</InputLabel>
        <Select
          value={selectedDiscount?.id || ""}
          label="Discount Type"
          sx={proposalSelectSx}
          onChange={handleDiscountSelect}
          disabled={isEditDisabled}
          MenuProps={proposalMenuProps}
        >
          <MenuItem value="">
            <em>Custom</em>
          </MenuItem>
          {discountProducts.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Amount */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <TextField
          size="small"
          type="number"
          placeholder="0"
          value={proposalDiscount || ""}
          onChange={(e) =>
            setProposalDiscount(
              e.target.value === "" ? 0 : Number(e.target.value)
            )
          }
          disabled={isEditDisabled}
          sx={{
            ...proposalTextFieldSx,
            width: { xs: "100%", sm: 160 },
            flex: { xs: 1, sm: "none" },
          }}
          InputProps={{
            startAdornment: (
              <Typography sx={{ fontSize: "0.75rem", mr: 0.5 }}>
                $
              </Typography>
            ),
          }}
        />
      </Box>

      <Typography
        sx={{
          fontSize: "0.75rem",
          opacity: 0.6,
          whiteSpace: "nowrap",
        }}
      >
        One-time discount applied to proposal total
      </Typography>
    </Stack>
  </Box>
</Box>

{/* -------------------- Monthly Service Charge (edits summary line below total) */}
<Box sx={{ mt: 2 }}>
  <Typography
    sx={{
      fontSize: "0.8rem",
      fontWeight: 600,
      mb: 1,
    }}
  >
    Monthly Service Charge
  </Typography>

  <Box
    sx={{
      p: 2,
      border: "1px solid",
      borderColor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(245,158,11,0.24)"
          : "rgba(245,158,11,0.22)",
      bgcolor: (theme) =>
        theme.palette.mode === "dark"
          ? "rgba(245,158,11,0.08)"
          : "rgba(245,158,11,0.04)",
      borderRadius: 2.5,
    }}
  >
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      alignItems={{ xs: "stretch", sm: "center" }}
    >
      <FormControl
        size="small"
        sx={{
          width: { xs: "100%", sm: "auto" },
          minWidth: { xs: "100%", sm: 520 },
          maxWidth: { xs: "100%", sm: 640 },
        }}
      >
        <InputLabel>Service type</InputLabel>
        <Select
          value={selectedMonthlyCharge?.id || ""}
          label="Service type"
          sx={proposalSelectSx}
          onChange={handleMonthlySelect}
          disabled={isEditDisabled}
          MenuProps={proposalMenuProps}
        >
          <MenuItem value="">
            <em>Custom</em>
          </MenuItem>
          {monthlyChargeProducts.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          width: { xs: "100%", sm: "auto" },
        }}
      >
        <TextField
          size="small"
          type="number"
          placeholder="0"
          value={monthlyServiceCharge ?? ""}
          onChange={(e) =>
            setMonthlyServiceCharge(
              e.target.value === "" ? null : Number(e.target.value)
            )
          }
          disabled={isEditDisabled}
          sx={{
            ...proposalTextFieldSx,
            width: { xs: "100%", sm: 160 },
            flex: { xs: 1, sm: "none" },
          }}
          InputProps={{
            startAdornment: (
              <Typography sx={{ fontSize: "0.75rem", mr: 0.5 }}>
                $
              </Typography>
            ),
          }}
        />
        <Typography
          sx={{
            fontSize: "0.75rem",
            opacity: 0.6,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          / month
        </Typography>
      </Box>

      <Typography
        sx={{
          fontSize: "0.75rem",
          opacity: 0.6,
          whiteSpace: { xs: "normal", sm: "nowrap" },
        }}
      >
        Shown in the table below the one-time total (not included in that total)
      </Typography>
    </Stack>
  </Box>
</Box>

<Box sx={{ mt: 2 }}>
  <Typography
    sx={{
      fontSize: "0.8rem",
      fontWeight: 600,
      mb: 1,
    }}
  >
    Special terms and promotions (optional)
  </Typography>
  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", mb: 1 }}>
    e.g. 1 month free monitoring, take over existing equipment, activation fee waived.
    Shown in the email after monthly monitoring and before the validity paragraph.
  </Typography>
  <TextField
    multiline
    minRows={3}
    fullWidth
    size="small"
    placeholder="List any special arrangements agreed with the customer"
    value={proposalSpecialTerms}
    onChange={(e) => setProposalSpecialTerms(e.target.value)}
    disabled={isEditDisabled}
    sx={{
      "& .MuiOutlinedInput-root": { fontSize: "0.875rem", borderRadius: 2 },
    }}
  />
</Box>

              {/* ---------------------------------------------Action Buttons */}
              <Stack 
                direction={{ xs: "column", sm: "row" }} 
                spacing={2} 
                justifyContent="space-between" 
                alignItems={{ xs: "stretch", sm: "center" }} 
                sx={{ mt: 3 }}
              >
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontStyle: "italic",
                    textAlign: { xs: "center", sm: "left" },
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  Line items save automatically. Click Save Draft to store pricing and terms.
                </Typography>
                <Stack 
                  direction={{ xs: "column", sm: "row" }} 
                  spacing={2}
                  sx={{ 
                    width: { xs: "100%", sm: "auto" },
                    order: { xs: 1, sm: 2 },
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={async () => {
                      try {
                        const saved = await saveProposalDraft();
                        if (!saved) return;
                        setShowProposal(false);
                        await fetchLead();
                      } catch (err) {
                        console.error("Error saving proposal draft:", err);
                        toast.error(
                          err?.response?.data?.detail || "Failed to save draft proposal."
                        );
                      }
                    }}
                    fullWidth={isMobile}
                    disabled={isEditDisabled}
                    sx={{
                      minHeight: 40,
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Save Draft
                  </Button>
                  {proposalItems.length > 0 && proposalStatus === "draft" && (
                    <Button
                      variant="contained"
                      color="info"
                      startIcon={<SendIcon />}
                      fullWidth={isMobile}
                      disabled={isEditDisabled}
                      sx={{
                        minHeight: 40,
                        borderRadius: "12px",
                        textTransform: "none",
                        fontWeight: 800,
                        boxShadow: "0 10px 24px rgba(2,132,199,0.28)",
                      }}
                      onClick={async () => {
                        if (isEditDisabled) return;
                        try {
                          const draftId = await ensureDraftProposalForNewFlow();
                          if (!draftId) {
                            toast.error("Unable to create draft proposal.");
                            return;
                          }
                          const response = await API.post(`/proposals/${draftId}/send/`, {
                            to_email: lead?.email || null,
                            monthly_service_charge: monthlyServiceCharge,
                            monthly_service_name:
                              selectedMonthlyCharge?.name ||
                              monthlyServiceLabel ||
                              "Monthly Monitoring Charge",
                            proposal_discount: proposalDiscount || 0,
                            discount_name: selectedDiscount?.name || "Discount",
                            message: "",
                            special_terms: proposalSpecialTerms || "",
                          });
                          setActiveProposalStatus("sent");
                          await fetchLead(); // Refresh data to get updated status
                          toast.success(`Proposal sent to ${response.data.to_email || lead?.email || "client"}!`);
                        } catch (err) {
                          console.error("Error sending proposal:", err);
                          const errorMsg = err.response?.data?.detail || "Failed to send proposal";
                          toast.error(errorMsg);
                        }
                      }}
                    >
                      Send Proposal
                    </Button>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        )}
{/* --------------------------------------------------Proposal Quick View */}
<Dialog
  open={quickViewOpen}
  onClose={closeQuickView}
  maxWidth="md"
  fullWidth
>
  {/* Header */}
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      px: 2,
      py: 1,
      borderBottom: "1px solid",
      borderColor: "divider",
    }}
  >
    <Typography fontWeight={600}>
      Proposal Preview
    </Typography>
    <IconButton size="small" onClick={closeQuickView}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>

  {/* Content */}
  <Box sx={{ 
    height: { xs: "50vh", sm: "60vh", md: "80vh" },
    position: "relative",
    overflow: "hidden",
  }}>
    {quickViewLoading && (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: { xs: "50vh", sm: "60vh", md: "80vh" },
        }}
      >
        <CircularProgress />
      </Box>
    )}

    {!quickViewLoading && quickViewError && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: { xs: "50vh", sm: "60vh", md: "80vh" },
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Failed to load proposal preview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Unable to display the proposal preview. Please try again later.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            lastPreviewProposal && openQuickView(lastPreviewProposal)
          }
        >
          Retry
        </Button>
      </Box>
    )}

    {!quickViewLoading && !quickViewError && quickViewHtml && (
      <Box
        sx={{
          height: "100%",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          p: { xs: 1, sm: 2 },
        }}
      >
        <iframe
          srcDoc={quickViewHtml}
          sandbox="allow-same-origin allow-scripts"
          style={{
            width: "100%",
            minHeight: "100%",
            border: "none",
            transform: isMobile ? "scale(0.65)" : "none",
            transformOrigin: "top left",
            pointerEvents: "auto",
          }}
          title="Proposal Preview"
        />
      </Box>
    )}

    {!quickViewLoading && !quickViewError && !quickViewHtml && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: { xs: "50vh", sm: "60vh", md: "80vh" },
          p: 3,
          textAlign: "center",
        }}
      >
        <Typography variant="body1" color="text.secondary">
          Proposal preview not available
        </Typography>
      </Box>
    )}
  </Box>
</Dialog>

      {/* --------------------------------------------------------Delete Confirmation */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Delete Lead"
        message={`Archive ${lead?.first_name} ${lead?.last_name}? They will be removed from active lists and can be restored later.`}
        confirmText="Yes, Delete"
        severity="error"
      />

      {/* ---------------------------------------------------------Restore Confirmation */}
      <ConfirmDialog
        open={confirmRestore}
        onClose={() => setConfirmRestore(false)}
        onConfirm={handleRestore}
        title="Restore Lead"
        message={`Are you sure you want to restore ${lead?.first_name} ${lead?.last_name}? This will make the lead active again.`}
        confirmText="Restore"
        severity="success"
      />

      <ConfirmDialog
        open={confirmWithdrawOpen}
        onClose={() => {
          setConfirmWithdrawOpen(false);
          setWithdrawProposalId(null);
        }}
        onConfirm={handleWithdrawProposal}
        title="Withdraw proposal?"
        message="Withdraw this proposal? The customer link will stop working."
        confirmText="Withdraw"
        severity="error"
      />
      </Box>
      </Box>
    </Box>
  );
}